import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Link, type Href } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import type { ReactNode } from 'react';
import type { GestureResponderEvent } from 'react-native';

import { Text } from '@/components/ui/text';
import { extractAppPath } from '@/utils/linkHelpers';

interface InlineMatch {
  end: number;
  innerText: string;
  marker?: string;
  start: number;
  type: 'format' | 'link';
  url?: string;
}

const FORMAT_CLASSES: Record<string, string[]> = {
  '**': ['font-bold'],
  '* *': ['italic'],
  __: ['underline'],
  '~~': ['line-through'],
  ';;': ['text-sm'],
};

const SUPERSCRIPT_MAP: Record<string, string> = {
  '⁰': '0',
  '¹': '1',
  '²': '2',
  '³': '3',
  '⁴': '4',
  '⁵': '5',
  '⁶': '6',
  '⁷': '7',
  '⁸': '8',
  '⁹': '9',
};

const getInlineMatches = (text: string): InlineMatch[] => {
  const matches: InlineMatch[] = [];

  const formatPattern = /(\*\*|__|~~|\* \*|;;)(.+?)\1/g;

  const linkPattern = /\[(.+?)\]\(([^()]*(?:\([^()]*\)[^()]*)*)\)/g;

  let match;

  while ((match = formatPattern.exec(text)) !== null) {
    matches.push({
      end: match.index + match[0].length,
      innerText: match[2],
      marker: match[1],
      start: match.index,
      type: 'format',
    });
  }

  while ((match = linkPattern.exec(text)) !== null) {
    matches.push({
      end: match.index + match[0].length,
      innerText: match[1],
      start: match.index,
      type: 'link',
      url: match[2],
    });
  }

  matches.sort((a, b) => a.start - b.start);

  return matches;
};

type TextSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl';

const stopPropagation = (e: GestureResponderEvent | React.MouseEvent) => e.stopPropagation();

const renderLink = (text: string, url: string, key: number, size?: TextSize): ReactNode => {
  const appPath = extractAppPath(url);

  const isInternalLink = appPath || url.startsWith('/');

  if (isInternalLink) {
    const href = (appPath || url) as Href;

    return (
      <Link href={href} key={key} onPress={stopPropagation} className="text-info-600 underline">
        {text}
      </Link>
    );
  }

  if (isWeb) {
    return (
      <a
        className="text-info-600 underline"
        href={url}
        key={key}
        onClick={stopPropagation}
        rel="noopener noreferrer"
        target="_blank">
        {text}
      </a>
    );
  }

  return (
    <Text
      className="text-info-600 underline"
      size={size}
      key={key}
      onPress={() => {
        WebBrowser.openBrowserAsync(url);
      }}>
      {text}
    </Text>
  );
};

const convertSuperscriptDigits = (text: string, keyOffset: number): ReactNode[] => {
  const nodes: ReactNode[] = [];

  const superscriptPattern = /[⁰¹²³⁴⁵⁶⁷⁸⁹]+/g;

  let lastIndex = 0;

  let match;

  let key = keyOffset;

  while ((match = superscriptPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    const digits = match[0]
      .split('')
      .map((char) => SUPERSCRIPT_MAP[char])
      .join('');

    nodes.push(<sup key={`sup-${keyOffset}-${match.index}-${key}`}>{digits}</sup>);

    key += 1;

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
};

export type ParseInlineMarkupOptions = {
  parentClasses?: string[];
  size?: TextSize;
};

/**
 * Parses inline markdown formatting and returns React nodes.
 * Supports: bold (**), italic (* *), underline (__), strikethrough (~~),
 * small text (;;), and links ([text](url)).
 *
 * For use in React components, prefer the `useInlineMarkup` hook which
 * memoizes the result. Use this function directly only in utility functions
 * that cannot use hooks.
 *
 * @param text - The markdown text to parse
 * @param options - Optional settings for formatting
 * @param options.parentClasses - CSS classes to inherit from parent formatting
 * @param options.size - Text size prop to apply to formatted Text components
 * @returns Array of React nodes with appropriate formatting
 */
export const parseInlineMarkup = (
  text: string,
  options: ParseInlineMarkupOptions = {}
): ReactNode[] => {
  const { parentClasses = [], size } = options;

  if (!text) {
    return [];
  }

  const matches = getInlineMatches(text);

  const nodes: ReactNode[] = [];

  let key = 0;

  let lastMatchEnd = 0;

  for (const match of matches) {
    if (match.start < lastMatchEnd) {
      continue;
    }

    if (match.start > lastMatchEnd) {
      key += 1;

      const textSegment = text.slice(lastMatchEnd, match.start);

      if (isWeb) {
        nodes.push(...convertSuperscriptDigits(textSegment, key));
      } else {
        nodes.push(textSegment);
      }
    }

    key += 1;

    if (match.type === 'link' && match.url) {
      nodes.push(renderLink(match.innerText, match.url, key, size));
    } else if (match.type === 'format' && match.marker) {
      const formatClasses = FORMAT_CLASSES[match.marker];

      if (formatClasses) {
        const nodeClasses = [...parentClasses, ...formatClasses];

        const childNodes = parseInlineMarkup(match.innerText, { parentClasses: nodeClasses, size });

        nodes.push(
          <Text className={nodeClasses.join(' ')} size={size} key={key}>
            {childNodes}
          </Text>
        );
      } else {
        const fallbackText = text.slice(match.start, match.end);

        if (isWeb) {
          nodes.push(...convertSuperscriptDigits(fallbackText, key));
        } else {
          nodes.push(fallbackText);
        }
      }
    }

    lastMatchEnd = match.end;
  }

  if (lastMatchEnd < text.length) {
    const remainingText = text.slice(lastMatchEnd);

    if (isWeb) {
      nodes.push(...convertSuperscriptDigits(remainingText, key));
    } else {
      nodes.push(remainingText);
    }
  }

  return nodes;
};
